import React, { useContext } from 'react';

import { Avatar, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { WhiteboardContext } from './context/WhiteboardContext';
import { WhiteboardSessionContext } from './context/WhiteboardSessionContext';
import { WhiteboardSessionActions } from './state/WhiteboardSessionState';
import { type Presenter } from './types';

export interface PresentingControlsProps {}

const PresentingControls: React.FC<PresentingControlsProps> = () => {
    const { userInfo, updatePresenter, updatePresenterPosition } = useContext(WhiteboardContext);
    const { socketState, sessionOptions, whiteboardSessionState, dispatchWhiteboardSessionAction, pushMessage } =
        useContext(WhiteboardSessionContext);
    const { t } = useTranslation(['whiteboard', 'common']);
    const { presenter, session, currentPage } = whiteboardSessionState;

    const handlePresent = () => {
        if (userInfo) {
            const presenterInfo: Presenter = {
                student_id: userInfo.student_id,
                userName: userInfo.userName,
                picUrl: userInfo.picUrl,
                role: userInfo.role,
                timestamp: Date.now(),
                currentPageId: currentPage?.page_id,
                pointer: {
                    x: 0,
                    y: 0
                }
            };
            updatePresenter(socketState.socket?.sessionId, session!, presenterInfo);
            dispatchWhiteboardSessionAction({
                type: WhiteboardSessionActions.Present,
                payload: { presenter: presenterInfo }
            });
        }
    };

    const handleStopPresenting = () => {
        updatePresenter(socketState.socket?.sessionId, session!, null);
        updatePresenterPosition(pushMessage, session!, null);
        dispatchWhiteboardSessionAction({
            type: WhiteboardSessionActions.Present,
            payload: { presenter: null }
        });
    };

    return (
        <>
            {presenter && (
                <>
                    <Avatar
                        sx={{ margin: '0 5px', width: '32px', height: '32px' }}
                        alt={presenter.userName}
                        src={presenter.picUrl}
                    />
                    <Typography sx={{ margin: '0 5px' }}>{t('isPresenting', { name: presenter.userName })}</Typography>
                </>
            )}

            {sessionOptions.canPresent && (
                <Button sx={{ margin: '0 5px' }} color="inherit" variant="outlined" onClick={handlePresent}>
                    {t('present')}
                </Button>
            )}

            {sessionOptions.canStopPresenting && (
                <Button sx={{ margin: '0 5px' }} color="error" variant="contained" onClick={handleStopPresenting}>
                    {t('stop')}
                </Button>
            )}
        </>
    );
};

export default PresentingControls;
